'use client';

import { useState } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { isSuperAdminSync } from '@/lib/permissions/isSuperAdmin.client';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Accept only root-relative paths as a post-login destination.
 *
 * This value comes from the query string, so an attacker can put anything in
 * it. Handing it straight to a navigation call turns the login page into an
 * open redirect — a phishing primitive, since the victim really did just
 * authenticate on the genuine site.
 *
 * Rejecting only a literal `//` prefix is not enough: the URL parser treats a
 * backslash as a slash while it is looking for the authority, so `/\evil.com`
 * resolves to `https://evil.com/` and leaves the origin.
 */
function safeInternalPath(value: string | null): string | null {
  if (!value) return null;

  // Must be root-relative. A leading space or control character fails this and
  // is rejected, which is the safe direction.
  if (!value.startsWith('/')) return null;

  // A second slash OR backslash begins an authority. The URL parser treats a
  // backslash as a slash while it looks for the host, so "/\evil.com" resolves
  // to https://evil.com/ — it slips past a check that only rejects "//".
  if (value.length > 1 && (value[1] === '/' || value[1] === '\\')) return null;

  return value;
}

export function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const { update } = useSession();

  // Where to go after a successful login, when something sent us here mid-flow.
  //
  // `callbackUrl` is what the OAuth authorize endpoint sends when it bounces an
  // unauthenticated user to log in; `returnUrl` is the older PWA deep-link
  // name. Reading only `returnUrl` meant the OAuth continuation was dropped
  // silently: the user logged in, landed on their dashboard, and the MCP client
  // waited for an authorization code that was never issued.
  const returnUrl = safeInternalPath(
    searchParams.get('callbackUrl') ?? searchParams.get('returnUrl')
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Email enviado', {
          description: data.data?.message || 'Revisa tu bandeja de entrada',
          duration: 5000,
        });
        setShowResendButton(false);
      } else {
        toast.error('Error', {
          description: data.error || 'No se pudo enviar el email',
          duration: 5000,
        });
      }
    } catch (error) {
      toast.error('Error', {
        description: 'Error al enviar el email de verificación',
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setShowResendButton(false);

    try {
      const result = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      });

      if (result?.error) {
        // Check if error is about email verification
        if (result.error.includes('verifica tu correo')) {
          setUserEmail(data.email);
          setShowResendButton(true);
          toast.error('Email no verificado', {
            description: result.error,
            duration: 6000,
          });
        } else {
          toast.error('Credenciales inválidas', {
            description: 'Por favor verifica tu email y contraseña',
          });
        }
        setIsLoading(false);
        return;
      }

      if (result?.ok) {
        toast.success('Inicio de sesión exitoso');

        // Update session to get latest user data
        const session = await update();

        // Resume whatever sent us to the login page (OAuth authorize, PWA deep
        // link). A full navigation rather than router.push: the target may be
        // an API route with no page component, which the client router cannot
        // navigate to, and the OAuth endpoint answers with a redirect.
        if (returnUrl) {
          window.location.href = returnUrl;
          return;
        }

        // Otherwise, redirect based on user type
        // SECURITY: Use explicit role-based check (cached in JWT)
        if (isSuperAdminSync(session)) {
          // Super Admin: redirect to tenant selector
          router.push('/select-tenant');
        } else if (session?.user?.tenantSlug) {
          // Regular user: redirect to their tenant
          router.push(`/${session.user.tenantSlug}`);
        } else {
          // Fallback: let middleware handle it
          router.push('/');
        }

        router.refresh();
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Error al iniciar sesión', {
        description: 'Ocurrió un error inesperado. Por favor intenta de nuevo.',
      });
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Iniciar Sesión</CardTitle>
        <CardDescription>
          Ingresa tus credenciales para acceder al sistema
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Correo Electrónico</Label>
            <Input
              id="email"
              type="email"
              placeholder="usuario@ejemplo.com"
              {...register('email')}
              disabled={isLoading}
              autoFocus
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
              disabled={isLoading}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Iniciando sesión...
              </>
            ) : (
              'Ingresar'
            )}
          </Button>

          {/* Resend Verification Button - shown when email is not verified */}
          {showResendButton && (
            <div className="mt-4 p-3 bg-brand-soft-peach border border-brand-line rounded-md">
              <p className="text-sm text-brand mb-2">
                Tu email no ha sido verificado. ¿No recibiste el correo?
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={handleResendVerification}
                disabled={resendingEmail}
              >
                {resendingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Reenviar Email de Verificación'
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
      <CardFooter className="flex flex-col space-y-2 border-t pt-4">
        <p className="text-sm text-center text-muted-foreground">
          ¿No tienes cuenta?{' '}
          <Link href="/signup" className="font-semibold text-primary underline-offset-4 hover:underline">
            Regístrate Gratis
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}
